import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  Autocomplete,
  CircularProgress,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Box,
  Button,
  Typography,
  IconButton,
  Grid,
  TextField,
  LinearProgress,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import VoiceWaveform from "../../../doctor-console/Utils/VoiceWaveform";
import { StyledButton } from "../../../doctor-console/StyledComponents";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import {
  normalizeVoiceCarePlanData,
  collectVoiceCarePlanProblems,
  formatVoiceCarePlanWarnings,
} from "../../utils/voiceCarePlanNormalize";
import useCarePlanServiceSearch, {
  getChargeSearchConfig,
} from "../../hooks/useCarePlanServiceSearch";
import CarePlanServiceAutocomplete from "../CarePlanServiceAutocomplete";
import usePharmacyStore from "../../hooks/usePharmacyStore";
import PharmacyStoreSelect from "../PharmacyStoreSelect";
import {
  searchChargeCaptureServices,
  investigationCategoryToGroupCode,
} from "../../../ipd-billing/billing-api";

// Hard cap on one dictation. Named because it was previously spelled three
// separate ways — 240000 for the auto-stop, 240 for the ring, and "4 minutes" in
// the message — so changing the limit meant finding all three.
const RECORDING_LIMIT_SECONDS = 240;

// ─── Structure-driven helpers (shared with CarePlanActivityModal schema) ───────
// The voice review form is rendered from the care_plan_structures template so it
// always matches whatever fields the backend/AI returns. These pure helpers build
// default values, read/write nested paths, and parse/format date-time values.

const labelize = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDefaultFieldValue = (field) => {
  if (!field) return "";
  if (field.type === "multi-select") return [];
  if (field.type === "table") return [];
  if (field.type === "boolean") return false;
  if (field.type === "number") return "";
  if (field.type === "object" || field.type === "group") {
    return buildInitialValues(field.structure || []);
  }
  return "";
};

function buildInitialValues(fields = []) {
  return fields.reduce((acc, field) => {
    if (!field?.field) return acc;
    acc[field.field] = getDefaultFieldValue(field);
    return acc;
  }, {});
}

// Merge template defaults with the AI/voice returned data so every structure field
// renders (empty if not extracted) while keeping any extracted values.
const deepMergeDefaults = (defaults, data) => {
  if (Array.isArray(defaults)) {
    return Array.isArray(data) ? data : defaults;
  }
  if (defaults && typeof defaults === "object") {
    const out = { ...defaults };
    if (data && typeof data === "object" && !Array.isArray(data)) {
      Object.keys(data).forEach((key) => {
        out[key] = deepMergeDefaults(defaults[key], data[key]);
      });
    }
    return out;
  }
  return data !== undefined ? data : defaults;
};

const getNestedValue = (source, path) =>
  path.reduce(
    (current, key) =>
      current && typeof current === "object" ? current[key] : undefined,
    source
  );

const updateNestedValue = (source, path, value) => {
  const cloned = Array.isArray(source) ? [...source] : { ...(source || {}) };
  let cursor = cloned;
  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    const nextValue =
      cursor[key] && typeof cursor[key] === "object"
        ? Array.isArray(cursor[key])
          ? [...cursor[key]]
          : { ...cursor[key] }
        : {};
    cursor[key] = nextValue;
    cursor = nextValue;
  }
  cursor[path[path.length - 1]] = value;
  return cloned;
};

const pad2 = (n) => String(n).padStart(2, "0");

const parseDateValue = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateForApi = (date, includeTime) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  if (includeTime) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate()
    )}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatTimeForApi = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const parseTimeFieldValue = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const hm = s.match(/^(\d{1,2}):(\d{2})/);
  if (hm) {
    const d = new Date(2000, 0, 1, Number(hm[1]), Number(hm[2]), 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`2000-01-01T${s}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getStructureId = (structure) =>
  Number(
    structure?.care_plan_id || structure?.id || structure?.template_id || 0
  ) || 0;

// ─── Voice → structure data normalization ─────────────────────────────────────
// The AI voice response does not always key test data as the "test_items" table
// the new template expects (it may return tests[]/test/testName/orderDetail/etc).
// These helpers reshape the returned data so the spoken tests actually populate
// the Test table rows (the same way medication.drug_name already populates).

// Field names that may hold a test's name across voice response variants.
const TEST_NAME_FIELDS = [
  "test_name",
  "testName",
  "name",
  "title",
  "orderDetail",
  "order_detail",
  "order",
  "investigation",
  "investigation_name",
  "investigationName",
  "procedure",
  "procedure_name",
  "test",
  "label",
  "display",
  "value",
];

// Container keys whose values (arrays/objects/strings) hold test entries.
const TEST_CONTAINER_KEYS = [
  "testitems",
  "tests",
  "orders",
  "investigations",
  "labtests",
  "diagnostics",
  "orderitems",
  "items",
  "procedures",
  "testlist",
];

const SPLIT_REGEX = /,| and |&|\/|\n|;/gi;

// Keywords that indicate a radiology/imaging test. Anything else defaults to
// laboratory (doctor can still change the per-row Category dropdown).
const RADIOLOGY_KEYWORDS = [
  "x-ray",
  "x ray",
  "xray",
  "radiograph",
  "ct ",
  "ct-",
  "ct scan",
  "cect",
  "hrct",
  "mri",
  "ultrasound",
  "usg",
  "sonograph",
  "doppler",
  "mammogram",
  "mammography",
  "angiogram",
  "angiography",
  "fluoroscopy",
  "pet scan",
  "pet-ct",
  "dexa",
  "bone scan",
  "barium",
  "ivp",
  "hsg",
  "kub",
  "scan",
  "imaging",
];

const detectTestCategory = (name = "") => {
  const text = String(name || "").toLowerCase();
  if (!text) return "laboratory";
  if (RADIOLOGY_KEYWORDS.some((kw) => text.includes(kw))) return "radiology";
  return "laboratory";
};

const pickTestName = (obj) => {
  for (const field of TEST_NAME_FIELDS) {
    const raw = obj?.[field];
    if (raw != null && String(raw).trim()) return String(raw).trim();
  }
  // Fallback: first non-empty string property.
  for (const value of Object.values(obj || {})) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

// Carry catalogue ids from add_ipd_voice_careplan when the backend already
// resolved them. Voice submit must NOT call /services/ to look them up — if
// they are missing, the doctor picks the test manually on the review screen.
const serviceMetaFromVoiceRow = (src, name) => {
  if (src?.__service_meta && typeof src.__service_meta === "object") {
    return src.__service_meta;
  }
  const medicalServiceId = String(
    src?.medical_service_id ?? src?.medicalServiceId ?? ""
  ).trim();
  const code = String(src?.service_code ?? src?.code ?? "").trim();
  if (!medicalServiceId && !code) return undefined;
  return {
    test_name: {
      name: name || src?.name || "",
      medical_service_id: medicalServiceId,
      code,
      rate: src?.rate ?? src?.fee ?? null,
      charge_modifiable:
        src?.chargeModifiable === true || src?.charge_modifiable === true,
      quantity_modifiable:
        src?.quantityModifiable !== false && src?.quantity_modifiable !== false,
    },
  };
};

const normalizeMatchCandidates = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (!c || typeof c !== "object") return null;
      const name = String(c.name || c.test_name || c.term || "").trim();
      const medical_service_id = String(
        c.medical_service_id ?? c.medicalServiceId ?? c.id ?? ""
      ).trim();
      if (!name && !medical_service_id) return null;
      return {
        ...c,
        name: name || medical_service_id,
        medical_service_id,
        service_code: String(c.service_code ?? c.code ?? "").trim(),
        code: String(c.service_code ?? c.code ?? "").trim(),
        rate: c.rate ?? c.fee ?? null,
      };
    })
    .filter(Boolean);
};

// add_ipd_voice_careplan already resolves the spoken test names against the
// chargemaster and returns the result NEXT TO the raw string rather than inside
// it (main/htai_service/careplan_service_matcher.py):
//
//   data.test_items      "Spine MRI, Urine routine examination, ..."
//   data.matched_services [{ name, service_id, matched_name, match_score }, ...]
//
// Nothing here read that array, so every dictated investigation reached the
// review screen as "Not in catalogue" — including the ones the backend had
// matched outright — and collectTestProblems blocked the save on all of them.
//
// Indexed by the spoken term, not by position: the backend splits on commas
// only while SPLIT_REGEX also breaks on " and ", "/" and ";", so the two lists
// are not always the same length. Entries the backend could not resolve
// (service_id null) are left out — those rows must still go to the picker.
const buildMatchedServiceIndex = (data) => {
  const index = new Map();
  const list = Array.isArray(data?.matched_services) ? data.matched_services : [];
  list.forEach((entry) => {
    const term = normalizeKey(entry?.name);
    const serviceId = String(entry?.service_id ?? "").trim();
    if (!term || !serviceId || index.has(term)) return;
    index.set(term, entry);
  });
  return index;
};

// Make a matched row look exactly like one the doctor picked by hand, so
// toTestItem builds its __service_meta and collectTestProblems lets the save
// through.
// A row that already carries an id wins — a doctor's correction must never be
// overwritten by the AI's match on a re-render.
const applyMatchedService = (src, matchedIndex) => {
  if (!matchedIndex || matchedIndex.size === 0) return src;
  const row =
    src && typeof src === "object" && !Array.isArray(src)
      ? src
      : { test_name: String(src || "").trim() };
  if (String(row.medical_service_id ?? row.medicalServiceId ?? "").trim()) return row;

  const spoken = pickTestName(row);
  const match = matchedIndex.get(normalizeKey(spoken));
  if (!match) return row;

  return {
    ...row,
    // The chargemaster name is what is ordered and billed, so it is what the
    // doctor reviews; the dictated wording is kept as raw_test_name for the
    // payload.
    test_name: String(match.matched_name || spoken).trim(),
    raw_test_name: String(row.raw_test_name || spoken).trim(),
    medical_service_id: String(match.service_id),
    match_status: "matched",
  };
};

const toTestItem = (src) => {
  if (src && typeof src === "object") {
    const name = pickTestName(src);
    const matchStatus = String(src.match_status || "").trim().toLowerCase();
    const matchCandidates = normalizeMatchCandidates(src.match_candidates);
    const item = {
      test_name: name,
      raw_test_name: String(src.raw_test_name || src.rawTestName || "").trim(),
      category: normalizeCategoryValue(src.category) || detectTestCategory(name),
      test_description:
        src.test_description || src.testDescription || src.description || "",
      emergency: Boolean(src.emergency || src.isEmergency || src.stat),
      instruction: src.instruction || src.instructions || "",
      match_status: matchStatus || undefined,
      match_candidates: matchCandidates,
    };
    const medicalServiceId = String(
      src.medical_service_id ?? src.medicalServiceId ?? ""
    ).trim();
    const code = String(src.service_code ?? src.code ?? "").trim();
    if (medicalServiceId) item.medical_service_id = medicalServiceId;
    if (code) item.service_code = code;
    if (src.rate != null || src.fee != null) item.rate = src.rate ?? src.fee;

    const meta = serviceMetaFromVoiceRow(src, name);
    if (meta) {
      item.__service_meta = meta;
      if (!item.match_status) item.match_status = "matched";
    } else if (!item.match_status) {
      item.match_status = matchCandidates.length > 1 ? "ambiguous" : "unmatched";
    }
    return item;
  }
  const name = String(src || "").trim();
  return {
    test_name: name,
    category: detectTestCategory(name),
    test_description: "",
    emergency: false,
    instruction: "",
    match_status: "unmatched",
    match_candidates: [],
  };
};

const isTestContainerKey = (key) => {
  const nk = normalizeKey(key);
  return (
    TEST_CONTAINER_KEYS.includes(nk) ||
    nk.includes("test") ||
    nk.includes("investigation") ||
    nk.includes("procedure")
  );
};

// Deep-scan the voice data for test entries regardless of how they are nested.
// Only invoked for test-type plans, so grabbing names within test containers is safe.
const deepExtractTestItems = (data, matchedIndex) => {
  const items = [];
  const makeItem = (src) => toTestItem(applyMatchedService(src, matchedIndex));
  const visit = (node, keyHint) => {
    if (node == null) return;
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, keyHint));
      return;
    }
    if (typeof node === "object") {
      const hasExplicitName =
        (node.test_name && String(node.test_name).trim()) ||
        (node.testName && String(node.testName).trim());
      if (hasExplicitName) {
        items.push(makeItem(node));
      } else if (isTestContainerKey(keyHint)) {
        const name = pickTestName(node);
        if (name) items.push(makeItem({ ...node, test_name: name }));
      }
      Object.entries(node).forEach(([k, v]) => visit(v, k));
      return;
    }
    if (typeof node === "string" && node.trim() && isTestContainerKey(keyHint)) {
      node
        .split(SPLIT_REGEX)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((name) => items.push(makeItem({ test_name: name })));
    }
  };
  visit(data, "");
  return items;
};

// Build the test_items[] rows from whatever shape the voice response used.
const buildTestItemsFromVoiceData = (data = {}) => {
  let items = [];
  // Catalogue matches the backend already resolved, folded onto each row as
  // it is built, so a matched test needs neither the picker nor a /services/ call.
  const matchedIndex = buildMatchedServiceIndex(data);
  const makeItem = (src) => toTestItem(applyMatchedService(src, matchedIndex));

  // Legacy HTAI: comma-separated string — split locally so the review table
  // still fills even before the EMR enricher ships array-of-objects.
  if (typeof data.test_items === "string" && data.test_items.trim()) {
    items = data.test_items
      .split(SPLIT_REGEX)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((name) => makeItem({ test_name: name }));
  } else if (Array.isArray(data.test_items) && data.test_items.length > 0) {
    items = data.test_items.map(makeItem);
  } else if (Array.isArray(data.tests) && data.tests.length > 0) {
    items = data.tests.map(makeItem);
  } else if (Array.isArray(data.orders) && data.orders.length > 0) {
    items = data.orders.map(makeItem);
  } else if (data.test && typeof data.test === "object") {
    items = [makeItem(data.test)];
  } else if (data.test_name || data.testName || data.name) {
    items = [
      makeItem({
        test_name: data.test_name || data.testName || data.name,
        test_description: data.test_description || data.description,
        instruction: data.instruction || data.instructions,
        emergency: data.emergency,
      }),
    ];
  } else {
    const orderText = String(
      data.orderDetail || data.order_detail || data.description || ""
    ).trim();
    if (orderText) {
      items = orderText
        .split(SPLIT_REGEX)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((name) => makeItem({ test_name: name }));
    }
  }

  // Last resort: deep-scan the entire payload for anything test-shaped.
  if (items.filter((item) => item.test_name).length === 0) {
    items = deepExtractTestItems(data, matchedIndex);
  }

  // Keep only named rows and drop duplicates by display name.
  const seen = new Set();
  return items.filter((item) => {
    const key = normalizeKey(item.test_name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeCategoryValue = (rawCategory) => {
  const cat = String(
    Array.isArray(rawCategory) ? rawCategory[0] : rawCategory || ""
  ).toLowerCase();
  if (!cat) return "";
  if (cat.includes("radio")) return "radiology";
  if (cat.includes("lab")) return "laboratory";
  if (["laboratory", "radiology", "other"].includes(cat)) return cat;
  return "other";
};

// Reshape the raw voice data into the field names the matched structure expects.
const normalizeVoiceDataForStructure = (structure, planType, rawData) => {
  const data = rawData && typeof rawData === "object" ? { ...rawData } : {};
  const nameKey = normalizeKey(structure?.name);
  const isTest = planType === "test" || nameKey === "test";

  if (isTest) {
    const items = buildTestItemsFromVoiceData(data);
    if (items.length > 0) data.test_items = items;
    // Transport only — the matches now live on the rows, and care_plans/ stores
    // `data` verbatim, so leaving it here would persist the raw AI bookkeeping
    // into the clinical record alongside the reviewed order.
    delete data.matched_services;
    // Plan-level category radio is hidden (per-row category is used instead), but
    // keep a valid value for the API — prefer any returned value, else derive it.
    const normalizedCategory = normalizeCategoryValue(data.category);
    data.category =
      normalizedCategory || items[0]?.category || "laboratory";
    if (!data.remark && (data.notes || data.clinicalIndication)) {
      data.remark = data.notes || data.clinicalIndication;
    }
  }

  const isMedication = planType === "medication" || nameKey === "medication";
  if (isMedication) {
    // Map the voice medication shape (name/period/periodUnit) onto the keys
    // create_medication_order() actually reads (drug_name/duration/
    // duration_unit). Done HERE, before the review renders, so the doctor sees
    // and can correct the values that will really be scheduled — normalising at
    // save time would hide a wrong duration behind a form that looked right.
    return normalizeVoiceCarePlanData(data);
  }

  return data;
};

const isEmptyish = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

// Whether a structure field actually holds captured data (used to hide the empty
// fields/tables of a plan so the review shows only what was spoken/added).
const fieldHasData = (field, value) => {
  if (!field) return false;
  switch (field.type) {
    case "table":
      return (
        Array.isArray(value) &&
        value.some(
          (row) =>
            row &&
            typeof row === "object" &&
            Object.values(row).some((cell) => !isEmptyish(cell) && cell !== false)
        )
      );
    case "multi-select":
      return Array.isArray(value) && value.length > 0;
    case "boolean":
      return value === true;
    case "object":
    case "group":
      return (field.structure || []).some((nested) =>
        fieldHasData(nested, value?.[nested.field])
      );
    default:
      return !isEmptyish(value);
  }
};

// Align a table row's keys to the column field names (case/format-insensitive).
const alignRowToColumns = (columns = [], row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return row;
  const colByKey = new Map();
  columns.forEach((col) => {
    if (col?.field) colByKey.set(normalizeKey(col.field), col.field);
  });
  const out = {};
  Object.entries(row).forEach(([rawKey, value]) => {
    const canonicalKey = colByKey.get(normalizeKey(rawKey)) || rawKey;
    if (out[canonicalKey] === undefined || isEmptyish(out[canonicalKey])) {
      out[canonicalKey] = value;
    }
  });
  return out;
};

// Recursively remap voice-data keys to the structure's canonical field names so
// AI-returned data populates the correct fields even when key formatting differs
// (e.g. historyOfPresentIllness ↔ history_of_present_illness). Unmatched keys are
// preserved so nothing is silently dropped.
const alignDataToStructure = (structureFields, data) => {
  if (
    !Array.isArray(structureFields) ||
    !data ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return data;
  }

  const fieldByKey = new Map();
  structureFields.forEach((field) => {
    if (field?.field) fieldByKey.set(normalizeKey(field.field), field);
  });

  const aligned = {};
  Object.entries(data).forEach(([rawKey, value]) => {
    const field = fieldByKey.get(normalizeKey(rawKey));
    const canonicalKey = field ? field.field : rawKey;

    if (field) {
      if (
        (field.type === "object" || field.type === "group") &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        aligned[canonicalKey] = alignDataToStructure(field.structure || [], value);
        return;
      }
      if (field.type === "table" && Array.isArray(value)) {
        aligned[canonicalKey] = value.map((row) =>
          alignRowToColumns(field.columns || [], row)
        );
        return;
      }
    }

    if (aligned[canonicalKey] === undefined || isEmptyish(aligned[canonicalKey])) {
      aligned[canonicalKey] = value;
    }
  });

  return aligned;
};

const VoiceAssistantModal = ({
  open,
  onClose,
  onSubmit,
  patientId,
  // [LAB_TEST][BILLING] Scopes the charge-capture search to this admission, the
  // same way the typed Investigation tab does.
  admissionId,
  carePlanStructures = [],
}) => {
  const [language, setLanguage] = useState("en");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [timerKey, setTimerKey] = useState(0);
  const [limitMessage, setLimitMessage] = useState("");
  const [step, setStep] = useState("record");
  const [reviewPlans, setReviewPlans] = useState([]);
  const [voiceWarnings, setVoiceWarnings] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputMode, setInputMode] = useState("voice");
  const [transcriptText, setTranscriptText] = useState("");

  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const limitTimeoutRef = useRef(null);
  const elapsedRef = useRef(0);
  const recordingStartRef = useRef(null);

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
    { code: "mix", name: "Mixed Language" },
  ];

  // Format remaining seconds as MM:SS
  const formatMMSS = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      handleStopRecording();
      setAudioBlob(null);
      setLimitMessage("");
      setLanguage("en");
      setIsPaused(false);
      setStep("record");
      setReviewPlans([]);
      setVoiceWarnings([]);
      setIsSubmitting(false);
      setInputMode("voice");
      setTranscriptText("");
    }
  }, [open]);

  // Object URL for the playback preview. Built here rather than inline in the
  // <audio src> because that ran on EVERY render, minting a fresh blob URL each
  // time and never revoking one — the modal leaked a recording per keystroke in
  // the review form. Matches how components/VoiceAssistant/VoiceAssistant.js
  // already handles its preview.
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  useEffect(() => {
    if (!audioBlob) {
      setAudioPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(audioBlob);
    setAudioPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  // [PHARMACY_RX] Stock is per store, so the review needs the same store picker
  // the typed form has — without one the OUT OF STOCK badge stays off, because a
  // stock answer that belongs to no counter is worse than none.
  const pharmacyStore = usePharmacyStore();

  const getApiHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    }),
    []
  );

  // [BILLING][PHARMACY_RX] The SAME engine CarePlanActivityModal runs, so a
  // dictated drug searches the same catalogue, against the same store, and
  // writes the same __billing_charge a typed one does.
  const serviceSearch = useCarePlanServiceSearch({
    getApiHeaders,
    pharmacyStoreId: pharmacyStore.storeId,
  });

  const toLabel = (value = "") =>
    String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const normalizeType = (rawType = "") => {
    const key = String(rawType || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const aliasMap = {
      medication: "medication",
      medicine: "medication",
      medicines: "medication",
      test: "test",
      tests: "test",
      lab: "test",
      labs: "test",
      note: "clinical-notes",
      notes: "clinical-notes",
      clinicalnotes: "clinical-notes",
      clinicalnote: "clinical-notes",
      monitoring: "monitoring",
      vitals: "monitoring",
      nutrition: "diet",
      diet: "diet",
      appointment: "appointment",
      appointments: "appointment",
      task: "task-list",
      tasks: "task-list",
      tasklist: "task-list",
    };
    return aliasMap[key] || "";
  };

  const getValueByPath = (source, path = []) =>
    path.reduce(
      (current, key) => (current && typeof current === "object" ? current[key] : undefined),
      source
    );

  const inferCarePlanType = (plan) => {
    const normalizedExisting = normalizeType(plan?.type);
    if (normalizedExisting) return normalizedExisting;
    const normalizedName = normalizeType(
      plan?.name || plan?.activity_name || plan?.activityType || ""
    );
    if (normalizedName) return normalizedName;

    const existingType = String(plan?.type || "").trim().toLowerCase();
    if (existingType && existingType !== "activity") return existingType;
    const data = plan?.data || {};
    if (Array.isArray(data?.medications) || data?.medication) return "medication";
    if (Array.isArray(data?.tests) || data?.test) return "test";
    if (data?.testName || data?.test_name || data?.clinicalIndication || data?.clinical_indication) {
      return "test";
    }
    const categoryValue = Array.isArray(data?.category)
      ? data.category.map((v) => String(v).toLowerCase()).join(" ")
      : String(data?.category || "").toLowerCase();
    if (categoryValue.includes("lab") || categoryValue.includes("test")) return "test";
    if (data?.orderDetail || data?.order_detail) return "test";
    if (Array.isArray(data?.monitoringItems)) return "monitoring";
    if (Array.isArray(data?.tasks)) return "task-list";
    if (data?.dietType || data?.feedingMode) return "diet";
    if (data?.start_datetime || data?.recurrence_enabled) return "appointment";
    const dataKeys = Object.keys(data || {}).map((k) => String(k).toLowerCase());
    if (dataKeys.some((k) => k.includes("test"))) return "test";
    return "activity";
  };

  // Maps the voice review "type" (from inferCarePlanType) to a care_plan_structures
  // template name so we can look up the correct field schema.
  const TYPE_TO_STRUCTURE_NAME = {
    medication: "medication",
    test: "test",
    "clinical-notes": "clinical notes",
    monitoring: "note",
    diet: "nutrition",
    appointment: "appointment",
    "task-list": "task",
  };

  // Resolve the care_plan_structures definition for a given voice plan.
  // Priority: exact care_plan_id/template_id match → name/type match → mapped name → partial.
  const findStructureForPlan = useCallback(
    (plan) => {
      if (!Array.isArray(carePlanStructures) || carePlanStructures.length === 0)
        return null;

      const planStructureId = Number(plan?.care_plan_id || 0);
      if (planStructureId) {
        const byId = carePlanStructures.find(
          (s) => getStructureId(s) === planStructureId
        );
        if (byId) return byId;
      }

      const planKey = normalizeKey(plan?.type || plan?.name);
      if (planKey) {
        const byName = carePlanStructures.find(
          (s) =>
            normalizeKey(s?.name) === planKey ||
            normalizeKey(s?.type) === planKey
        );
        if (byName) return byName;

        const mappedName = TYPE_TO_STRUCTURE_NAME[plan?.type];
        if (mappedName) {
          const mappedKey = normalizeKey(mappedName);
          const byMapped = carePlanStructures.find(
            (s) => normalizeKey(s?.name) === mappedKey
          );
          if (byMapped) return byMapped;
        }

        const partial = carePlanStructures.find((s) => {
          const nameKey = normalizeKey(s?.name);
          return (
            nameKey &&
            (nameKey.includes(planKey) || planKey.includes(nameKey))
          );
        });
        if (partial) return partial;
      }

      return null;
    },
    [carePlanStructures]
  );

  // Write a value at a nested path within a review plan's data object.
  const setPlanFieldValue = (planIndex, path, value) => {
    setReviewPlans((prev) =>
      prev.map((plan, idx) => {
        if (idx !== planIndex) return plan;
        const nextData = updateNestedValue(plan.data || {}, path, value);
        return { ...plan, data: nextData };
      })
    );
  };

  const deleteReviewPlan = (planIndex) => {
    setReviewPlans((prev) => prev.filter((_, idx) => idx !== planIndex));
  };

  // Append a fresh blank entry for a group (e.g. add another Medication), using
  // the group's structure so it renders the same editable form as the AI entries.
  const addBlankEntryForSection = (section) => {
    const structure = section?.structure;
    const referenceType =
      section?.entries?.[0]?.plan?.type ||
      (structure
        ? inferCarePlanType({ type: structure.name, name: structure.name })
        : "activity");
    const newPlan = {
      type: referenceType,
      name: structure?.name || section?.title || "",
      care_plan_id: structure ? getStructureId(structure) || undefined : undefined,
      data: structure ? buildInitialValues(structure.structure || []) : {},
    };
    setReviewPlans((prev) => [...prev, newPlan]);
  };

  // Human-friendly title for a single review entry (accordion header).
  const getPlanEntryLabel = (plan, structure, index) => {
    const data = plan?.data || {};
    const planType = inferCarePlanType(plan);
    const baseName = structure?.name || labelize(planType);

    // Tests list every order inside the table, so the entry header stays generic
    // (e.g. "Test 1") instead of repeating the first test name.
    if (planType === "test" || normalizeKey(structure?.name) === "test") {
      return `${baseName} ${index + 1}`;
    }

    const med = data?.medication;
    if (med && typeof med === "object" && (med.drug_name || med.name)) {
      return med.drug_name || med.name;
    }
    if (data?.drug_name) return data.drug_name;
    if (data?.doctor || data?.specialty) {
      return [data.doctor, data.specialty].filter(Boolean).join(" • ");
    }
    return `${baseName} ${index + 1}`;
  };

  // ── Structure-driven field renderer (mirrors CarePlanActivityModal.renderField) ──
  const renderStructureField = (
    planIndex,
    field,
    sourceData,
    pathPrefix = [],
    hideEmpty = false
  ) => {
    if (!field?.field) return null;
    const path = [...pathPrefix, field.field];
    const key = path.join(".");
    const fieldValue = getNestedValue(sourceData, path);
    const label = field.label || labelize(field.field);
    const isRequired = field.required === true;
    const onFieldChange = (targetPath, value) =>
      setPlanFieldValue(planIndex, targetPath, value);

    // In "captured-only" mode (e.g. Clinical Notes), skip fields with no data so
    // the review shows only what the doctor actually dictated.
    if (hideEmpty && !fieldHasData(field, fieldValue)) return null;

    if (field.type === "object") {
      return (
        <Grid item xs={12} key={key}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderStructureField(planIndex, nestedField, sourceData, path, hideEmpty)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "group") {
      // Only render a group when its order_type/condition matches the current data.
      if (field.condition) {
        const condValue = getNestedValue(sourceData, [field.condition.field]);
        if (condValue !== field.condition.value) return null;
      }
      return (
        <Grid item xs={12} key={key}>
          <Paper variant="outlined" sx={{ p: 2, mt: 0.5, borderRadius: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mb: 1.5, color: "#121959", fontSize: "0.88rem" }}
            >
              {field.label || label}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderStructureField(planIndex, nestedField, sourceData, path, hideEmpty)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "boolean") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(e) => onFieldChange(path, e.target.checked)}
              />
            }
            label={label}
          />
        </Grid>
      );
    }

    if (field.type === "select") {
      const options = Array.isArray(field.options) ? field.options : [];
      // Reference selects (e.g. specialty/doctor) have no static options — fall
      // back to an editable text field so the extracted value is preserved.
      if (options.length === 0) {
        return (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              fullWidth
              size="small"
              label={label}
              required={isRequired}
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            />
          </Grid>
        );
      }
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControl fullWidth size="small" required={isRequired}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              label={label}
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {labelize(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "multi-select") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControl fullWidth size="small" required={isRequired}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              multiple
              label={label}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(e) => onFieldChange(path, e.target.value)}
              renderValue={(selected) =>
                (selected || []).map((item) => labelize(item)).join(", ")
              }
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {labelize(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "radio") {
      // Tests now carry a per-row Category column, so the plan-level Category
      // radio is redundant — hide it (its data value is still kept for the API).
      if (normalizeKey(field.field) === "category" && pathPrefix.length === 0) {
        return null;
      }
      return (
        <Grid item xs={12} key={key}>
          <FormControl required={isRequired}>
            <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              {label}
              {isRequired ? " *" : ""}
            </FormLabel>
            <RadioGroup
              row
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: "0.83rem" }}>{labelize(opt)}</Typography>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "datetime") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseDateValue(fieldValue)}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, true) : "")
            }
            showTimeSelect
            timeIntervals={15}
            dateFormat="dd-MM-yyyy h:mm aa"
            timeCaption="Time"
            placeholderText="dd-mm-yyyy --:--"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "date") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseDateValue(fieldValue)}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, false) : "")
            }
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-mm-yyyy"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "time") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseTimeFieldValue(fieldValue)}
            onChange={(date) => onFieldChange(path, date ? formatTimeForApi(date) : "")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "table") {
      const rows = Array.isArray(fieldValue) ? fieldValue : [];
      const baseColumns = field.columns || [];
      // For the tests table, add a per-row Category column (auto-detected from the
      // test name, but editable) so lab vs radiology is marked on each test.
      const isTestItemsTable =
        field.field === "test_items" ||
        baseColumns.some((col) => col.field === "test_name");
      const hasCategoryColumn = baseColumns.some(
        (col) => normalizeKey(col.field) === "category"
      );
      let columns = baseColumns;
      if (isTestItemsTable && !hasCategoryColumn) {
        const categoryColumn = {
          field: "category",
          label: "Category",
          type: "select",
          options: ["laboratory", "radiology", "other"],
        };
        const testNameIdx = baseColumns.findIndex(
          (col) => col.field === "test_name"
        );
        columns =
          testNameIdx >= 0
            ? [
                ...baseColumns.slice(0, testNameIdx + 1),
                categoryColumn,
                ...baseColumns.slice(testNameIdx + 1),
              ]
            : [categoryColumn, ...baseColumns];
      }
      // Voice review: always use the catalogue picker for the test name so
      // unmatched / ambiguous EMR enricher rows can be fixed without leaving
      // the modal — even if the structure declares a plain text column.
      if (isTestItemsTable) {
        columns = columns.map((col) => {
          const nk = normalizeKey(col.field);
          if (
            nk === "testname" ||
            nk === "name" ||
            nk === "orderdetail" ||
            nk === "order_detail"
          ) {
            return { ...col, type: "dynamic-select" };
          }
          return col;
        });
      }
      const emptyRow = () =>
        columns.reduce(
          (acc, col) => ({ ...acc, [col.field]: col.type === "boolean" ? false : "" }),
          {}
        );
      const addRow = () => onFieldChange(path, [...rows, emptyRow()]);
      const removeRow = (rowIdx) =>
        onFieldChange(path, rows.filter((_, i) => i !== rowIdx));
      const changeCell = (rowIdx, colField, value) =>
        onFieldChange(
          path,
          rows.map((row, i) => (i === rowIdx ? { ...row, [colField]: value } : row))
        );

      // [LAB_TEST][BILLING] Attaches the picked charge-master row to the cell as
      // __service_meta — the same carrier the typed Investigation tab writes, and
      // what buildLabTestsFromEntries reads for medical_service_id/code. Without
      // it a dictated investigation still reaches the lab dashboard (the
      // plain-field fallback) but carries no service id, so it cannot be billed.
      const selectCellService = (rowIdx, colField, value, item) =>
        onFieldChange(
          path,
          rows.map((row, i) => {
            if (i !== rowIdx) return row;
            const meta = { ...(row.__service_meta || {}) };
            const serviceId = item?.medical_service_id ?? item?.id ?? "";
            const code = String(
              item?.service_code ??
                item?.code ??
                item?.conceptId ??
                item?.concept_id ??
                item?.snomed_code ??
                "",
            );
            if (serviceId || code || item?.name || item?.term) {
              meta[colField] = {
                name: item?.name || item?.term || value,
                medical_service_id: String(serviceId),
                code,
                rate: item?.rate ?? item?.fee ?? null,
                charge_modifiable: item?.chargeModifiable === true || item?.charge_modifiable === true,
                quantity_modifiable: item?.quantityModifiable !== false && item?.quantity_modifiable !== false,
              };
            } else {
              delete meta[colField];
            }
            const matched = Boolean(serviceId || code);
            return {
              ...row,
              [colField]: value,
              medical_service_id: matched ? String(serviceId) : "",
              service_code: matched ? code : "",
              match_status: matched ? "matched" : "unmatched",
              match_candidates: matched ? [] : row.match_candidates || [],
              __service_meta: meta,
            };
          })
        );

      return (
        <Grid item xs={12} key={key}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#121959" }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: "none" }}>
              Add Row
            </Button>
          </Box>
          {rows.length > 0 ? (
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5fffe" }}>
                    {columns.map((col) => (
                      <TableCell key={col.field} sx={{ fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                        {col.label || labelize(col.field)}
                        {col.required ? " *" : ""}
                      </TableCell>
                    ))}
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIdx) => (
                    <TableRow key={rowIdx}>
                      {columns.map((col) => {
                        if (col.type === "boolean") {
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1, textAlign: "center" }}>
                              <Checkbox
                                size="small"
                                checked={Boolean(row[col.field])}
                                onChange={(e) => changeCell(rowIdx, col.field, e.target.checked)}
                                sx={{ p: 0 }}
                              />
                            </TableCell>
                          );
                        }
                        // [LAB_TEST][BILLING] Same catalogue search the typed
                        // Investigation tab runs on its dynamic-select columns.
                        if (col.type === "dynamic-select") {
                          const dynKey = `${planIndex}.${key}.${rowIdx}.${col.field}`;
                          const candidateItems = normalizeMatchCandidates(row.match_candidates);
                          const searchedItems = serviceSearch.chargeOptionsByKey[dynKey] || [];
                          // Prefer live search results; otherwise seed from EMR
                          // enricher match_candidates so ambiguous rows are
                          // pickable without another /services/ call.
                          const optionItems =
                            searchedItems.length > 0 ? searchedItems : candidateItems;
                          const dynOpts = Array.from(
                            new Set(optionItems.map((i) => i.name).filter(Boolean))
                          );
                          const isLoading = Boolean(serviceSearch.chargeLoadingByKey[dynKey]);
                          const groupData = getNestedValue(sourceData, pathPrefix) || {};
                          // Scope the catalogue search by the row's OWN Category
                          // cell. The plan-level category is a single hidden
                          // value seeded from the first dictated test, so a
                          // Laboratory row was searched with group_code=RADIOLOGY.
                          const hasRowCategory = columns.some(
                            (c) => normalizeKey(c.field) === "category"
                          );
                          const cellCategory = String(
                            (hasRowCategory ? row.category : groupData.category) || ""
                          );
                          const hasCategoryField =
                            hasRowCategory ||
                            Object.prototype.hasOwnProperty.call(groupData, "category");
                          const categoryRequired = hasCategoryField && !cellCategory;
                          const matchStatus = String(row.match_status || "").toLowerCase();
                          const hasService =
                            Boolean(row.__service_meta?.[col.field]?.medical_service_id) ||
                            Boolean(String(row.medical_service_id || "").trim());
                          let matchHelper = "";
                          if (categoryRequired) {
                            matchHelper = "Select category first";
                          } else if (!hasService && matchStatus === "ambiguous") {
                            matchHelper = candidateItems.length
                              ? "Multiple matches — pick one from the list"
                              : "Ambiguous match — search and pick the correct service";
                          }
                          const findOptionItem = (selectedName) =>
                            optionItems.find(
                              (i) => (i.name || i.term || "") === selectedName
                            );
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 180 }}>
                              <Autocomplete
                                freeSolo
                                size="small"
                                fullWidth
                                options={dynOpts}
                                loading={isLoading}
                                value={row[col.field] ?? ""}
                                onInputChange={(_, val, reason) => {
                                  // Only real keystrokes hit /services/. Mount
                                  // (reset) and programmatic clears must not.
                                  if (reason === "clear") {
                                    selectCellService(rowIdx, col.field, "", null);
                                    return;
                                  }
                                  if (reason !== "input") return;
                                  changeCell(rowIdx, col.field, val);
                                  if (categoryRequired) return;
                                  if (!val || val.length < 2) return;
                                  // "other" maps to null, i.e. no group_code —
                                  // lab AND radiology are both searched.
                                  const groupCode = investigationCategoryToGroupCode(
                                    cellCategory.toLowerCase()
                                  );
                                  serviceSearch.setChargeLoadingByKey((p) => ({ ...p, [dynKey]: true }));
                                  searchChargeCaptureServices(val, {
                                    limit: 50,
                                    admissionId,
                                    groupCode,
                                  })
                                    .then((items) => {
                                      serviceSearch.setChargeOptionsByKey((p) => ({
                                        ...p,
                                        [dynKey]: items.map((i) => ({
                                          ...i,
                                          name: i.name || i.term || i.service_name || i.label || "",
                                          medical_service_id: i.medical_service_id ?? i.id,
                                          service_code: i.service_code ?? i.code,
                                          code: i.service_code ?? i.code,
                                        })),
                                      }));
                                    })
                                    .catch(() => {})
                                    .finally(() =>
                                      serviceSearch.setChargeLoadingByKey((p) => ({ ...p, [dynKey]: false }))
                                    );
                                }}
                                onChange={(_, val) => {
                                  const selectedName = typeof val === "string" ? val : val || "";
                                  const picked = findOptionItem(selectedName);
                                  selectCellService(rowIdx, col.field, selectedName, picked);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    required={col.required}
                                    placeholder={`Search ${labelize(col.field)}…`}
                                    error={Boolean(categoryRequired || (!hasService && matchStatus && matchStatus !== "matched"))}
                                    helperText={matchHelper}
                                    onBlur={(e) => {
                                      params.onBlur?.(e);
                                      const currentVal = row[col.field] || "";
                                      if (!currentVal || hasService) return;
                                      const matched = findOptionItem(currentVal);
                                      if (matched) selectCellService(rowIdx, col.field, currentVal, matched);
                                    }}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: isLoading ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        params.InputProps.endAdornment
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                          );
                        }
                        if (col.type === "select") {
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 160 }}>
                              <FormControl fullWidth size="small">
                                <Select
                                  displayEmpty
                                  value={row[col.field] ?? ""}
                                  onChange={(e) => changeCell(rowIdx, col.field, e.target.value)}
                                  sx={{ fontSize: "0.78rem" }}
                                >
                                  <MenuItem value="" disabled sx={{ fontSize: "0.78rem" }}>
                                    <em>Select…</em>
                                  </MenuItem>
                                  {(col.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                                      {labelize(opt)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 140 }}>
                            <TextField
                              size="small"
                              fullWidth
                              type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                              value={row[col.field] ?? ""}
                              onChange={(e) => changeCell(rowIdx, col.field, e.target.value)}
                              InputLabelProps={col.type === "date" ? { shrink: true } : undefined}
                              sx={{ "& .MuiInputBase-input": { fontSize: "0.78rem", py: "5px" } }}
                            />
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ py: 0.4, px: 0.5 }}>
                        <IconButton size="small" onClick={() => removeRow(rowIdx)} sx={{ color: "#ef4444" }}>
                          <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Box
              onClick={addRow}
              sx={{
                border: "1px dashed #d1d5db",
                borderRadius: 1,
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#21cdc0", backgroundColor: "#f5fffe" },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Click "Add Row" to add {String(label).toLowerCase()} entries
              </Typography>
            </Box>
          )}
        </Grid>
      );
    }

    const isMultiline = field.type === "textarea";
    const inputType = field.type === "number" ? "number" : "text";

    // [BILLING][PHARMACY_RX] Same catalogue picker the typed Care Plan form uses.
    // A dictated drug name was a bare TextField until now: no catalogue match, no
    // OUT OF STOCK badge, no Substitute, and — because __billing_charge is only
    // written when a catalogue item is picked — nothing for postBillingCharges to
    // bill. getChargeSearchConfig keys off the plan's own type, which is exactly
    // the activityName the typed form passes.
    const chargeSearchConfig = getChargeSearchConfig(
      field,
      String(reviewPlans[planIndex]?.type || "").toLowerCase(),
      pathPrefix
    );
    if (chargeSearchConfig && inputType === "text") {
      return (
        <CarePlanServiceAutocomplete
          key={key}
          searchKey={`${planIndex}.${key}`}
          search={serviceSearch}
          chargeSearchConfig={chargeSearchConfig}
          path={path}
          label={label}
          fieldValue={fieldValue}
          isRequired={isRequired}
          fieldError=""
          onFieldChange={onFieldChange}
        />
      );
    }

    return (
      <Grid item xs={12} sm={isMultiline ? 12 : 6} key={key}>
        <TextField
          fullWidth
          size="small"
          label={label}
          type={inputType}
          value={fieldValue ?? ""}
          multiline={isMultiline}
          rows={isMultiline ? 3 : undefined}
          required={isRequired}
          onChange={(e) => onFieldChange(path, e.target.value)}
        />
      </Grid>
    );
  };

  const prevRecordingRef = useRef(isRecording);

  // Detect transitions of isRecording to reset/start/stop timer
  useEffect(() => {
    // start -> increment key to ensure fresh timer instance
    if (isRecording && !prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    // stop -> increment key to reset (and the timer will be hidden because isRecording=false)
    if (!isRecording && prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    prevRecordingRef.current = isRecording;
  }, [isRecording]);

  const handleStartRecording = async () => {
    try {
      if (!window.isSecureContext) {
        setLimitMessage(
          "Microphone requires a secure connection. Please use HTTPS or localhost.",
        );
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setLimitMessage(
          "Microphone access is not supported in this browser/environment.",
        );
        return;
      }

      if (typeof window.MediaRecorder === "undefined") {
        setLimitMessage(
          "Audio recording is not supported in this browser/environment.",
        );
        return;
      }

      clearTimeout(limitTimeoutRef.current);
      elapsedRef.current = 0;
      recordingStartRef.current = Date.now();
      setLimitMessage("");

      try {
        if (
          navigator.permissions &&
          typeof navigator.permissions.query === "function"
        ) {
          const permissionStatus = await navigator.permissions.query({
            name: "microphone",
          });

          if (permissionStatus.state === "denied") {
            setLimitMessage(
              "Microphone permission is blocked. Please allow microphone access in browser site settings.",
            );
            return;
          }
        }
      } catch (permissionErr) {
        console.warn(
          "Could not query microphone permission state:",
          permissionErr,
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        clearTimeout(limitTimeoutRef.current);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsPaused(false);

      // Auto-stop after 4 minutes (240 seconds)
      limitTimeoutRef.current = setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
        }
        setIsRecording(false);
        setIsPaused(false);
        setLimitMessage("Recording limit reached (4 minutes).");
      }, RECORDING_LIMIT_SECONDS * 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        setLimitMessage(
          "Microphone permission denied. Please allow access in browser settings and try again.",
        );
      } else if (err?.name === "NotFoundError") {
        setLimitMessage(
          "No microphone device found. Please connect a microphone and retry.",
        );
      } else if (err?.name === "NotReadableError") {
        setLimitMessage(
          "Microphone is currently in use by another app/tab. Please close it and retry.",
        );
      } else if (err?.name === "AbortError") {
        setLimitMessage(
          "Microphone initialization was interrupted. Please try again.",
        );
      } else if (err?.name === "OverconstrainedError") {
        setLimitMessage(
          "Requested audio settings are not supported by this microphone.",
        );
      } else {
        setLimitMessage(
          "Failed to access microphone. Please check browser permissions and connection security.",
        );
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      }
      setIsRecording(false);
      setIsPaused(false);
      clearTimeout(limitTimeoutRef.current);
    }
  };

  const handlePauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.pause();
        const now = Date.now();
        elapsedRef.current += now - recordingStartRef.current;
        setIsPaused(true);
      }
    }
  };

  const handleResumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      if (mediaRecorderRef.current.state === "paused") {
        mediaRecorderRef.current.resume();
        recordingStartRef.current = Date.now();
        setIsPaused(false);
      }
    }
  };

  // Catalogue matching for investigations comes from add_ipd_voice_careplan
  // (medical_service_id on the row) or from the doctor typing into the picker.
  // Do not bulk-call /services/ after voice submit — that floods the API and
  // hits rate limits. Unmatched rows stay blocked by collectTestProblems until
  // the doctor picks them.

  // Voice and text both return care plans in the same shape; map them onto the
  // review step identically.
  const applyTranscribeResult = (result, emptyMessage) => {
    const plans = Array.isArray(result?.carePlans)
      ? result.carePlans
      : Array.isArray(result?.care_plans)
        ? result.care_plans
        : [];
    if (plans.length === 0) {
      setLimitMessage(emptyMessage);
      return;
    }
    const mappedPlans = plans.map((plan) => {
        const type = inferCarePlanType(plan);
        const structure = findStructureForPlan({ ...plan, type });
        const baseData =
          plan?.data && typeof plan.data === "object" ? plan.data : {};
        // Reshape voice data into the structure's field names (e.g. spoken
        // tests → test_items table rows) so nothing is dropped on review.
        const normalizedRaw = normalizeVoiceDataForStructure(
          structure,
          type,
          baseData
        );
        // Align returned keys to the template's canonical field names
        // (camelCase ↔ snake_case ↔ spaced), incl. nested objects & table rows.
        const alignedRaw = structure
          ? alignDataToStructure(structure.structure || [], normalizedRaw)
          : normalizedRaw;
        // Merge template defaults so every structure field renders (empty if
        // not extracted) while preserving the values the AI returned.
        const data = structure
          ? deepMergeDefaults(
              buildInitialValues(structure.structure || []),
              alignedRaw
            )
          : alignedRaw;
        // Diagnostic: if a Test plan produced no rows, log the raw shape so the
        // exact field the AI used can be mapped in buildTestItemsFromVoiceData.
        if (
          type === "test" &&
          (!Array.isArray(data?.test_items) || data.test_items.length === 0)
        ) {
          try {
            console.warn(
              "[VoiceReview] Test plan produced no test_items. Raw plan JSON:\n" +
                JSON.stringify(plan, null, 2)
            );
          } catch (_) {
            console.warn(
              "[VoiceReview] Test plan produced no test_items. Raw data:",
              baseData
            );
          }
        }
        return {
          ...plan,
          type,
          care_plan_id:
            Number(plan?.care_plan_id || 0) ||
            (structure ? getStructureId(structure) || undefined : undefined),
          data,
        };
    });

    setVoiceWarnings(
      formatVoiceCarePlanWarnings(
        result?.warnings || result?.data?.warnings || []
      )
    );
    setReviewPlans(mappedPlans);
    setStep("review");
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setLimitMessage("No recording available. Please record audio first.");
      return;
    }

    if (onSubmit) {
      setIsSubmitting(true);
      setLimitMessage("");
      try {
        const result = await onSubmit({
          mode: "transcribe",
          audioBlob,
          language,
        });
        applyTranscribeResult(
          result,
          "No care plans detected from voice. Please retry."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleTextSubmit = async () => {
    const transcript = transcriptText.trim();
    if (!transcript) {
      setLimitMessage("Please enter the care plan instructions first.");
      return;
    }

    if (onSubmit) {
      setIsSubmitting(true);
      setLimitMessage("");
      try {
        const result = await onSubmit({
          mode: "transcribe-text",
          transcript,
        });
        applyTranscribeResult(
          result,
          "No care plans detected from the text. Please rephrase and retry."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputModeChange = (_, nextMode) => {
    handleStopRecording();
    setLimitMessage("");
    setInputMode(nextMode);
  };

  const resolveActualEditPath = (plan, path = []) => {
    if (!Array.isArray(path) || path.length === 0) return path;
    const planType = inferCarePlanType(plan);

    if (planType === "medication") {
      const medicationKeyMap = {
        name: "name",
        frequency: "frequency",
        start: "start",
        end: "end",
        period: "period",
        periodUnit: "periodUnit",
        safety: "safety",
      };

      if (path[0] === "medications" && typeof path[1] === "number") {
        if (path[2] === "medication") {
          const nestedField = path[3];
          const mappedField = medicationKeyMap[nestedField] || nestedField;
          const selectedMedication = getValueByPath(plan?.data || {}, ["medications", path[1]]) || {};
          // Support both shapes:
          // 1) medications[i].frequency (flat)
          // 2) medications[i].medication.frequency (nested)
          if (selectedMedication?.medication && typeof selectedMedication.medication === "object") {
            return ["medications", path[1], "medication", mappedField];
          }
          return ["medications", path[1], mappedField];
        }
        if (
          ["priority", "intent", "category", "reason", "course_of_therapy", "notes"].includes(
            path[2]
          )
        ) {
          return [path[2]];
        }
      }

      // Single-medication shape: data.medication (not data.medications[]).
      // Legacy fallback mapping used only when no care_plan_structure matches a plan.
      if (path[0] === "medication") {
        if (path[1] === "medication") {
          const nestedField = path[2];
          const mappedField = medicationKeyMap[nestedField] || nestedField;
          const medicationRoot = getValueByPath(plan?.data || {}, ["medication"]) || {};
          // Support both shapes:
          // 1) data.medication.frequency (flat)
          // 2) data.medication.medication.frequency (nested)
          if (medicationRoot?.medication && typeof medicationRoot.medication === "object") {
            return ["medication", "medication", mappedField];
          }
          return ["medication", mappedField];
        }
        if (
          ["priority", "intent", "category", "reason", "course_of_therapy", "notes"].includes(
            path[1]
          )
        ) {
          return [path[1]];
        }
      }
    }

    if (planType === "test") {
      if (path[0] === "__test_name__") {
        // Synthetic item path used only for display grouping.
        // Persist edits on real top-level test payload.
        return path.length >= 3 ? [path[2]] : [];
      }

      if (path[0] === "tests" && typeof path[1] === "number") {
        if (["testName", "priority", "clinicalIndication", "frequency", "scheduledDateTime"].includes(path[2])) {
          return ["tests", path[1], path[2]];
        }
      }
    }

    return path;
  };

  const handleEditPlanDataByPath = (planIndex, path, value) => {
    setReviewPlans((prev) =>
      prev.map((plan, idx) => {
        if (idx !== planIndex) return plan;
        const actualPath = resolveActualEditPath(plan, path);
        if (!Array.isArray(actualPath) || actualPath.length === 0) return plan;
        const nextData = Array.isArray(plan.data) ? [...plan.data] : { ...(plan.data || {}) };
        let cursor = nextData;
        for (let i = 0; i < actualPath.length - 1; i += 1) {
          const key = actualPath[i];
          const existing = cursor[key];
          cursor[key] = Array.isArray(existing) ? [...existing] : { ...(existing || {}) };
          cursor = cursor[key];
        }
        cursor[actualPath[actualPath.length - 1]] = value;
        return { ...plan, data: nextData };
      })
    );
  };

  const renderEditableField = (planIndex, fieldKey, fieldValue, path = []) => {
    const keyPath = [...path, fieldKey];
    const fieldLabel = String(fieldKey || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    if (Array.isArray(fieldValue)) {
      return (
        <Grid item xs={12} key={keyPath.join(".")}>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {fieldLabel}
            </Typography>
            <Box sx={{ pl: 1, mt: 0.5, display: "flex", flexDirection: "column", gap: 1 }}>
              {fieldValue.map((item, idx) => {
                if (item && typeof item === "object") {
                  return (
                    <Paper key={`${keyPath.join(".")}.${idx}`} variant="outlined" sx={{ p: 1 }}>
                      <Grid container spacing={1}>
                        {Object.entries(item).map(([nestedKey, nestedVal]) =>
                          renderEditableField(planIndex, nestedKey, nestedVal, [
                            ...keyPath,
                            idx,
                          ])
                        )}
                      </Grid>
                    </Paper>
                  );
                }
                return (
                  <TextField
                    key={`${keyPath.join(".")}.${idx}`}
                    size="small"
                    fullWidth
                    label={`${fieldLabel} ${idx + 1}`}
                    value={item ?? ""}
                    onChange={(e) =>
                      handleEditPlanDataByPath(planIndex, [...keyPath, idx], e.target.value)
                    }
                  />
                );
              })}
            </Box>
          </Box>
        </Grid>
      );
    }

    if (fieldValue && typeof fieldValue === "object") {
      return (
        <Grid item xs={12} key={keyPath.join(".")}>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {fieldLabel}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
              <Grid container spacing={1}>
                {Object.entries(fieldValue).map(([nestedKey, nestedVal]) =>
                  renderEditableField(planIndex, nestedKey, nestedVal, keyPath)
                )}
              </Grid>
            </Paper>
          </Box>
        </Grid>
      );
    }

    if (typeof fieldValue === "boolean") {
      return (
        <Grid item xs={12} sm={6} key={keyPath.join(".")}>
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(e) =>
                  handleEditPlanDataByPath(planIndex, keyPath, e.target.checked)
                }
              />
            }
            label={fieldLabel}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={keyPath.join(".")}>
        <TextField
          size="small"
          fullWidth
          label={fieldLabel}
          value={fieldValue ?? ""}
          onChange={(e) => handleEditPlanDataByPath(planIndex, keyPath, e.target.value)}
          sx={{ mt: 0.5 }}
        />
      </Grid>
    );
  };

  // Blocking problems in what the doctor is about to save, recomputed on every
  // edit so the list clears as rows are fixed. These are the states care_plans/
  // either rejects outright (no drug name, unsupported frequency — the entry is
  // rolled back and only `skipped[]` reports it) or silently mangles (a missing
  // duration becomes a one-day course). Cheaper to catch here than on the ward.
  const reviewProblems = useMemo(
    () => collectVoiceCarePlanProblems(reviewPlans),
    [reviewPlans]
  );

  const handleSaveReviewedPlans = async () => {
    if (!onSubmit) return;
    if (reviewProblems.length > 0) return;
    setIsSubmitting(true);
    try {
      const editedPlans = reviewPlans.map((plan) => {
        return {
          type: plan.type,
          name: plan.name,
          care_plan_id: plan.care_plan_id,
          data: plan.data || {},
        };
      });

      const success = await onSubmit({
        mode: "persist",
        carePlans: editedPlans,
      });

      if (success !== false) {
        handleClose();
      }
    } catch (error) {
      setLimitMessage(error?.message || "Please fix invalid review data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSectionTitle = (plan) => {
    const normalized = inferCarePlanType(plan);
    const titleMap = {
      medication: "Medication",
      test: "Test",
      "clinical-notes": "Note",
      monitoring: "Monitoring",
      diet: "Nutrition",
      appointment: "Appointment",
      "task-list": "Task",
    };
    return titleMap[normalized] || toLabel(normalized || "Activity");
  };

  // Group review plans by their resolved care_plan_structure (falling back to the
  // inferred type). Each group renders one accordion; each plan an inner entry.
  const reviewGroups = useMemo(() => {
    const groups = new Map();
    reviewPlans.forEach((plan, planIndex) => {
      const structure = findStructureForPlan(plan);
      const key = structure
        ? `structure-${getStructureId(structure) || normalizeKey(structure.name)}`
        : `type-${inferCarePlanType(plan) || "activity"}`;
      const title = structure?.name || getSectionTitle(plan);
      if (!groups.has(key)) {
        groups.set(key, { key, title, structure, entries: [] });
      }
      groups.get(key).entries.push({ plan, planIndex });
    });
    return Array.from(groups.values());
  }, [reviewPlans, findStructureForPlan]);

  // Renders a single plan entry's editable fields (structure-driven, or the
  // generic fallback when no structure matched).
  const renderPlanFields = (section, plan, planIndex, hideEmpty) => (
    <Grid container spacing={1.5}>
      {section.structure
        ? (section.structure.structure || []).map((field) =>
            renderStructureField(planIndex, field, plan.data || {}, [], hideEmpty)
          )
        : Object.entries(plan.data || {}).map(([fieldKey, fieldValue]) =>
            renderEditableField(planIndex, fieldKey, fieldValue, [])
          )}
      {hideEmpty &&
        section.structure &&
        !(section.structure.structure || []).some((field) =>
          fieldHasData(field, getNestedValue(plan.data || {}, [field.field]))
        ) && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No notes captured from the recording.
            </Typography>
          </Grid>
        )}
    </Grid>
  );

  const handleClose = () => {
    handleStopRecording();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={step === "review" ? "lg" : "sm"}
      PaperProps={{
        sx:
          step === "review"
            ? {
              width: "92vw",
              maxWidth: "1200px",
              height: "92vh",
            }
            : {
              width: "100%",
              maxWidth: "640px",
            },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "1.2rem",
          color: "#125386",
        }}
      >
        Care Plan Assistant
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px" }}>
        {step === "record" ? (
          <>
            <Box
              sx={{
                backgroundColor: "#FFF8E1",
                border: "1px solid #FFECB3",
                borderRadius: "6px",
                padding: "0px 12px",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                color: "#795548",
                fontSize: "0.875rem",
                lineHeight: 1.4,
                marginBottom: 1.5,
              }}
            >
              <span style={{ fontSize: "18px", flexShrink: 0 }}>⚠️</span>
              <Typography
                component="span"
                sx={{
                  display: "inline",
                  flex: 1,
                  wordBreak: "break-word",
                  fontSize: "0.8rem",
                }}
              >
                AI-generated content may contain transcription or interpretation
                errors. Please review and correct all details before submitting.
              </Typography>
            </Box>

            <Tabs
              value={inputMode}
              onChange={handleInputModeChange}
              variant="fullWidth"
              sx={{ mb: 2, borderBottom: "1px solid #e0e0e0" }}
            >
              <Tab
                value="voice"
                label="Voice Assistant"
                icon={<MicIcon fontSize="small" />}
                iconPosition="start"
                disabled={isSubmitting}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                value="text"
                label="Text Assistant"
                icon={<KeyboardIcon fontSize="small" />}
                iconPosition="start"
                disabled={isSubmitting}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>

            {inputMode === "text" ? (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Care plan instructions"
                  placeholder="e.g. Prescribe Paracetamol 500mg twice daily for 3 days. Schedule a follow-up with Cardiology on high priority."
                  value={transcriptText}
                  onChange={(e) => setTranscriptText(e.target.value)}
                  disabled={isSubmitting}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <StyledButton
                    bgColor="#5DAA60"
                    onClick={handleTextSubmit}
                    disabled={!transcriptText.trim() || isSubmitting}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      "&:hover": { backgroundColor: "#1b5e20" },
                    }}
                  >
                    {isSubmitting ? "Processing..." : "Submit"}
                  </StyledButton>
                </Box>
                {isSubmitting && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.75, color: "#555" }}
                    >
                      Building the care plan — this usually takes a few seconds.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
            <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              {/* Labelled: an unlabelled box gave no clue it chose the dictation
                  language, and a wrong language is the single biggest cause of a
                  useless transcript. */}
              <InputLabel id="voice-language-label">Dictation language</InputLabel>
              <Select
                labelId="voice-language-label"
                label="Dictation language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isRecording}
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: { style: { maxHeight: 200, width: "auto" } },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Grid container justifyContent="space-between">
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={8}
                  display="flex"
                  justifyContent="flex-start"
                  gap={1}
                >
                  <Grid item xs={6} md={6} lg={5}>
                    <StyledButton
                      bgColor="#0E3F66"
                      onClick={handleStartRecording}
                      disabled={isRecording}
                      variant="contained"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": { backgroundColor: "#07367dff" },
                      }}
                    >
                      Start Recording
                    </StyledButton>
                  </Grid>
                  <Grid item xs={6} md={6} lg={4}>
                    <StyledButton
                      bgColor={isPaused ? "#5DAA60" : "#FFA500"}
                      onClick={isPaused ? handleResumeRecording : handlePauseRecording}
                      disabled={!isRecording}
                      variant="contained"
                      fullWidth
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": {
                          backgroundColor: isPaused ? "#1b5e20" : "#FF8C00",
                        },
                      }}
                    >
                      {isPaused ? "Resume" : "Pause"}
                    </StyledButton>
                  </Grid>

                  <Grid item xs={6} md={6} lg={5}>
                    <StyledButton
                      bgColor="#FF3B30"
                      onClick={handleStopRecording}
                      disabled={!isRecording}
                      variant="contained"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": { backgroundColor: "#b71c1c" },
                      }}
                    >
                      Stop Recording
                    </StyledButton>
                  </Grid>
                </Grid>

                <Grid item xs={6} md={6} lg={3}>
                  <StyledButton
                    bgColor="#5DAA60"
                    onClick={handleSubmit}
                    disabled={isRecording || !audioBlob || isSubmitting}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      width: "90%",
                      height: "90%",
                      "&:hover": { backgroundColor: "#1b5e20" },
                    }}
                  >
                    {isSubmitting ? "Processing..." : "Submit"}
                  </StyledButton>
                </Grid>
              </Grid>
            </Box>

            {/* A 4-minute dictation can take a while to come back; the button
                label alone left the screen looking frozen. */}
            {isSubmitting && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.75, color: "#555" }}
                >
                  Transcribing and building the care plan — this usually takes 20–40 seconds.
                </Typography>
              </Box>
            )}

            {isRecording && streamRef.current && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <VoiceWaveform
                      stream={streamRef.current}
                      isRecording={isRecording}
                      isPaused={isPaused}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: 110,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CountdownCircleTimer
                      key={timerKey}
                      isPlaying={isRecording && !isPaused}
                      duration={RECORDING_LIMIT_SECONDS}
                      size={90}
                      strokeWidth={8}
                      colors={["#5DAA60", "#55C1A1", "#00A3E0", "#0E3F66"]}
                      colorsTime={[RECORDING_LIMIT_SECONDS, 160, 80, 0]}
                      onComplete={() => {
                        return { shouldRepeat: false, delay: 0 };
                      }}
                    >
                      {({ remainingTime }) => (
                        // Elapsed, not remaining. A doctor thinks "how long have
                        // I been talking", and the ring already shows how much of
                        // the 4-minute cap is left. The cap is spelled out so a
                        // cut-off is never a surprise.
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.2, textAlign: "center" }}
                        >
                          {formatMMSS(RECORDING_LIMIT_SECONDS - remainingTime)}
                          <Typography
                            component="span"
                            sx={{ display: "block", fontSize: "0.62rem", fontWeight: 500, color: "#666" }}
                          >
                            / {formatMMSS(RECORDING_LIMIT_SECONDS)}
                          </Typography>
                        </Typography>
                      )}
                    </CountdownCircleTimer>
                  </Box>
                </Box>
              </Box>
            )}

            {audioPreviewUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Preview:
                </Typography>
                <audio controls src={audioPreviewUrl} />
              </Box>
            )}
            </>
            )}
          </>
        ) : (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              Review AI Generated Prescriptions
            </Typography>
            {/* [PHARMACY_RX] Stock is per store. Shown whenever a medication is
                under review so the doctor can see availability before saving. */}
            {reviewPlans.some(
              (plan) => String(plan?.type || "").toLowerCase() === "medication"
            ) && (
              <Box sx={{ mb: 1.5 }}>
                <PharmacyStoreSelect
                  stores={pharmacyStore.stores}
                  storeId={pharmacyStore.storeId}
                  onChange={pharmacyStore.setStoreId}
                  loading={pharmacyStore.loading}
                  error={pharmacyStore.error}
                  showPicker={pharmacyStore.showPicker}
                />
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              {reviewGroups.map((section) => {
                // Clinical Notes: show only the fields the doctor actually dictated
                // (hide the empty template fields/tables), like tests/medication.
                const hideEmpty =
                  normalizeKey(section.structure?.name) === "clinicalnotes";
                // Tests list every order in one table, so skip the per-entry
                // accordion header and render straight from the Orders table.
                const flatEntries =
                  normalizeKey(section.structure?.name) === "test";
                return (
                  <Accordion key={section.key} defaultExpanded disableGutters sx={{ mb: 1.25, border: "1px solid #e0e0e0", borderRadius: 1, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0.5 }}>
                      {!flatEntries && section.structure && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mb: 1,
                          }}
                        >
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBlankEntryForSection(section)}
                            sx={{
                              fontSize: "0.73rem",
                              textTransform: "none",
                              color: "#21cdc0",
                            }}
                          >
                            Add {section.title}
                          </Button>
                        </Box>
                      )}
                      {flatEntries
                        ? section.entries.map(({ plan, planIndex }) => (
                            <Box key={`entry-${section.key}-${planIndex}`} sx={{ mb: 1 }}>
                              {section.entries.length > 1 && (
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deleteReviewPlan(planIndex)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                              {renderPlanFields(section, plan, planIndex, hideEmpty)}
                            </Box>
                          ))
                        : section.entries.map(({ plan, planIndex }, entryIndex) => (
                            <Accordion
                              key={`entry-${section.key}-${planIndex}`}
                              defaultExpanded={section.entries.length === 1}
                              disableGutters
                              sx={{
                                mb: 1,
                                border: "1px solid #ececec",
                                borderRadius: 1,
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    pr: 1,
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {getPlanEntryLabel(plan, section.structure, entryIndex)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteReviewPlan(planIndex);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                {renderPlanFields(section, plan, planIndex, hideEmpty)}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
            <Divider sx={{ my: 1 }} />
            {voiceWarnings.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  p: 1.25,
                  border: "1px solid #fde68a",
                  borderRadius: 1,
                  backgroundColor: "#fffbeb",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400e" }}>
                  Voice review notes ({voiceWarnings.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {voiceWarnings.map((warning) => (
                    <Typography
                      component="li"
                      key={warning}
                      sx={{ fontSize: "0.8rem", color: "#92400e", lineHeight: 1.5 }}
                    >
                      {warning}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {/* Kept next to Save rather than in the dialog-footer limitMessage:
                that sits below the fold on a long review and was being missed. */}
            {reviewProblems.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  p: 1.25,
                  border: "1px solid #f5c2c7",
                  borderRadius: 1,
                  backgroundColor: "#fdf2f3",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#842029" }}>
                  Fix before saving ({reviewProblems.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {reviewProblems.map((problem) => (
                    <Typography
                      component="li"
                      key={problem}
                      sx={{ fontSize: "0.8rem", color: "#842029", lineHeight: 1.5 }}
                    >
                      {problem}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setStep("record")}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveReviewedPlans}
                disabled={
                  isSubmitting ||
                  reviewPlans.length === 0 ||
                  reviewProblems.length > 0
                }
              >
                {isSubmitting ? "Saving..." : "Save Reviewed Plans"}
              </Button>
            </Box>
          </Box>
        )}

        {limitMessage && (
          // Boxed rather than a centred line of red text: microphone-permission
          // and recording-limit messages are the ones a doctor has to act on,
          // and as plain text at the foot of a scrolled dialog they were missed.
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              border: "1px solid #f5c2c7",
              borderRadius: 1,
              backgroundColor: "#fdf2f3",
              color: "#842029",
              fontSize: "0.82rem",
              lineHeight: 1.5,
            }}
          >
            {limitMessage}
          </Box>
        )}
      </DialogContent>

      <DialogActions />
    </Dialog>
  );
};

export default VoiceAssistantModal;

